import { useState } from "react";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Trash2, Shield, Clock, CheckCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useLocale } from "@/i18n/context";
import { localizedPath } from "@/i18n/routes";

export default function DataDeletion() {
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const { toast } = useToast();
  const { t, locale } = useLocale();
  const copy = t.legal.dataDeletion;
  const homePath = localizedPath("home", locale);

  /**
   * Submitting an erasure request under GDPR Art. 17 starts a one-month clock
   * (Art. 12(3)). So the success screen may only appear once the request has
   * actually reached us — showing it on a failed send would tell the person
   * their request is being handled while it sits nowhere.
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      toast({
        title: t.common.error,
        description: copy.toastEmailRequired,
        variant: "destructive",
      });
      return;
    }

    setSending(true);
    try {
      const { error } = await supabase.functions.invoke("send-email", {
        body: {
          template_key: "data_deletion_request",
          recipient_email: "info@nreport.ai",
          variables: {
            requester_email: email.trim(),
            reason: reason.trim() || "—",
            requested_at: new Date().toLocaleString("sl-SI"),
          },
        },
      });
      if (error) throw error;

      setSubmitted(true);
      toast({
        title: copy.toastSuccessTitle,
        description: copy.toastSuccessDesc,
      });
    } catch (err) {
      console.error("Data deletion request failed:", err);
      toast({
        title: t.common.error,
        description: copy.toastSendFailed,
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
        <Header />
        <div className="container mx-auto px-4 py-16 pt-24">
          <div className="max-w-2xl mx-auto text-center">
            <div className="mb-8">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h1 className="text-3xl font-bold mb-4">{copy.successTitle}</h1>
              <p className="text-muted-foreground">{copy.successDesc}</p>
            </div>

            <Card>
              <CardContent className="pt-6">
                <div className="space-y-4 text-left">
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <p className="font-medium">{copy.processingTitle}</p>
                      <p className="text-sm text-muted-foreground">{copy.processingDesc}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Shield className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <p className="font-medium">{copy.securityTitle}</p>
                      <p className="text-sm text-muted-foreground">{copy.securityDesc}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Button onClick={() => (window.location.href = homePath)} className="mt-6">
              {copy.backHome}
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      <Header />
      <div className="container mx-auto px-4 py-16 pt-24">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <Trash2 className="h-16 w-16 text-primary mx-auto mb-4" />
            <h1 className="text-3xl font-bold mb-4">{copy.title}</h1>
            <p className="text-muted-foreground">{copy.subtitle}</p>
          </div>

          <Alert className="mb-8">
            <Shield className="h-4 w-4" />
            <AlertDescription>{copy.alert}</AlertDescription>
          </Alert>

          <Card>
            <CardHeader>
              <CardTitle>{copy.formTitle}</CardTitle>
              <CardDescription>{copy.formDesc}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email">{copy.emailLabel}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder={copy.emailPlaceholder}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <p className="text-xs text-muted-foreground">{copy.emailHint}</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reason">{copy.reasonLabel}</Label>
                  <Textarea
                    id="reason"
                    placeholder={copy.reasonPlaceholder}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={4}
                  />
                </div>

                <div className="bg-muted/50 p-4 rounded-lg">
                  <h3 className="font-medium mb-2">{copy.deletedDataTitle}</h3>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {copy.deletedDataItems.map((item) => (
                      <li key={item}>• {item}</li>
                    ))}
                  </ul>
                </div>

                <Button type="submit" className="w-full" variant="destructive" disabled={sending}>
                  {sending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4 mr-2" />
                  )}
                  {sending ? t.common.sending : copy.submit}
                </Button>
              </form>
            </CardContent>
          </Card>

          <div className="mt-8 text-center">
            <p className="text-sm text-muted-foreground">
              {copy.contact}{" "}
              <a href="mailto:info@nreport.ai" className="text-primary hover:underline">
                info@nreport.ai
              </a>
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
